import {
  CreditCard,
  Mail,
  Phone,
  Cloud,
  LogIn,
  Github,
  Truck,
  Map,
  MapPin,
  Send,
  Instagram,
  MessageCircle,
  PlayCircle,
  type LucideIcon
} from 'lucide-react'

interface IntegrationIconProps {
  integrationType: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

// SVG Logos for AI services
const OpenAILogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
  </svg>
)

const AnthropicLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 30 30" fill="currentColor">
    <rect x="11" y="3" width="3" height="24" rx="1.5" />
    <rect x="16" y="3" width="3" height="24" rx="1.5" />
  </svg>
)

const GoogleGeminiLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="gemini-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4285F4" />
        <stop offset="50%" stopColor="#9B72CB" />
        <stop offset="100%" stopColor="#D96570" />
      </linearGradient>
    </defs>
    <circle cx="7" cy="7" r="2.5" fill="url(#gemini-gradient)" />
    <circle cx="17" cy="7" r="2.5" fill="url(#gemini-gradient)" />
    <circle cx="7" cy="17" r="2.5" fill="url(#gemini-gradient)" />
    <circle cx="17" cy="17" r="2.5" fill="url(#gemini-gradient)" />
  </svg>
)

// Map integration types to their icons
const INTEGRATION_ICON_MAP: Record<string, LucideIcon> = {
  stripe: CreditCard,
  resend: Mail,
  twilio: Phone,
  sendgrid: Mail,
  aws_s3: Cloud,
  google_oauth: LogIn,
  github_oauth: Github,
  samsara: Truck,
  wex: CreditCard,
  here_maps: Map,
  google_maps: MapPin,
  telegram: Send,
  instagram: Instagram,
  whatsapp: MessageCircle,
  youtube: PlayCircle
}

// Map integration types to their brand colors
const INTEGRATION_COLOR_MAP: Record<string, string> = {
  openai: 'text-[#10A37F] bg-[#10A37F]/10',
  anthropic: 'text-[#191919] bg-[#D4A574]/20',
  google_gemini: 'text-[#4285F4] bg-[#4285F4]/10',
  stripe: 'text-[#635BFF] bg-[#635BFF]/10',
  resend: 'text-[#000000] bg-gray-100',
  twilio: 'text-[#F22F46] bg-[#F22F46]/10',
  sendgrid: 'text-[#1A82E2] bg-[#1A82E2]/10',
  aws_s3: 'text-[#FF9900] bg-[#FF9900]/10',
  google_oauth: 'text-[#4285F4] bg-[#4285F4]/10',
  github_oauth: 'text-[#24292e] bg-gray-100',
  samsara: 'text-[#FF6B00] bg-[#FF6B00]/10',
  wex: 'text-[#005DAA] bg-[#005DAA]/10',
  here_maps: 'text-[#48DAD0] bg-[#48DAD0]/10',
  google_maps: 'text-[#4285F4] bg-[#4285F4]/10',
  telegram: 'text-[#0088cc] bg-[#0088cc]/10',
  instagram: 'text-[#E4405F] bg-[#E4405F]/10',
  whatsapp: 'text-[#25D366] bg-[#25D366]/10',
  youtube: 'text-[#FF0000] bg-[#FF0000]/10'
}

const SIZE_MAP = {
  sm: {
    container: 'p-1.5',
    icon: 'h-4 w-4'
  },
  md: {
    container: 'p-2',
    icon: 'h-5 w-5'
  },
  lg: {
    container: 'p-3',
    icon: 'h-6 w-6'
  }
}

export function IntegrationIcon({ integrationType, size = 'md', className = '' }: IntegrationIconProps) {
  const colorClasses = INTEGRATION_COLOR_MAP[integrationType] || 'text-gray-600 bg-gray-100'
  const sizeClasses = SIZE_MAP[size]

  // Render SVG logos for AI services
  if (integrationType === 'openai') {
    return (
      <div className={`inline-flex items-center justify-center rounded-lg ${sizeClasses.container} ${colorClasses} ${className}`}>
        <OpenAILogo className={sizeClasses.icon} />
      </div>
    )
  }

  if (integrationType === 'anthropic') {
    return (
      <div className={`inline-flex items-center justify-center rounded-lg ${sizeClasses.container} ${colorClasses} ${className}`}>
        <AnthropicLogo className={sizeClasses.icon} />
      </div>
    )
  }

  if (integrationType === 'google_gemini') {
    return (
      <div className={`inline-flex items-center justify-center rounded-lg ${sizeClasses.container} ${colorClasses} ${className}`}>
        <GoogleGeminiLogo className={sizeClasses.icon} />
      </div>
    )
  }

  // Default to Lucide icon for other integrations
  const Icon = INTEGRATION_ICON_MAP[integrationType] || CreditCard

  return (
    <div className={`inline-flex items-center justify-center rounded-lg ${sizeClasses.container} ${colorClasses} ${className}`}>
      <Icon className={sizeClasses.icon} />
    </div>
  )
}
